/*
  This file is currently redundant because the platform state is backed 
  by the same data source. There is no need to merge the state since the
  same information is broadcasted on both the OB and RFQ websockets. 
  Attempting to merge OB.state and RFQ.state would be redundant and only 
  serve the split race condition between received events.

  Alternatives:
  1. We can use the latest unique state event received across RFQ and OB
  2. We can listen to a single source of truth (RFQ) and ignore OB since
     they contain the same information
*/

import { ReactNode } from 'react';
import createSafeContext from '@paradigm/utils/createSafeContext';
import {
  razzmatazz,
  paradigmLightBlue,
  neonLemon,
  grapefruit,
} from '@paradigm/design-system/src/colors';
import {
  ParsedPlatformStateEnum,
  parsedPlatformStateValues,
} from '@paradigm/api/types';
import { FetcherStatus } from '#/positions/contexts/abstractions/useFetcher';
import {
  PlatformStateView,
  useDrfqPlatformStateView,
  useGrfqPlatformStateView,
} from './store';

interface Props {
  children: ReactNode;
}

export function MergedPlatformStateProvider({ children }: Props) {
  const drfqPlatformStateView = useDrfqPlatformStateView();
  const grfqPlatformStateView = useGrfqPlatformStateView();
  const view = createView(drfqPlatformStateView, grfqPlatformStateView);

  return (
    <MergedPlatformStateFetcherContext value={view}>
      {children}
    </MergedPlatformStateFetcherContext>
  );
}

const [useUMPlatformStateView, MergedPlatformStateFetcherContext] =
  createSafeContext<PlatformStateView>('MergedPlatformStateView');

export { useUMPlatformStateView };

const getMergedState = (
  drfqState: ParsedPlatformStateEnum,
  grfqState: ParsedPlatformStateEnum,
): ParsedPlatformStateEnum => {
  if (
    drfqState === parsedPlatformStateValues.UNAVAILABLE ||
    grfqState === parsedPlatformStateValues.UNAVAILABLE
  )
    return parsedPlatformStateValues.UNAVAILABLE;
  if (
    drfqState === parsedPlatformStateValues.AVAILABLE_CANCEL_ONLY ||
    grfqState === parsedPlatformStateValues.AVAILABLE_CANCEL_ONLY
  )
    return parsedPlatformStateValues.AVAILABLE_CANCEL_ONLY;
  return parsedPlatformStateValues.AVAILABLE;
};

const getLatestDate = (
  drfqDate?: string,
  grfqDate?: string,
): string | undefined => {
  if (drfqDate === undefined) return grfqDate;
  if (grfqDate === undefined) return drfqDate;
  return new Date(drfqDate) > new Date(grfqDate) ? drfqDate : grfqDate;
};

const getEarlierDate = (
  drfqDate?: string,
  grfqDate?: string,
): string | undefined => {
  if (drfqDate === undefined) return grfqDate;
  if (grfqDate === undefined) return drfqDate;
  return new Date(drfqDate) < new Date(grfqDate) ? drfqDate : grfqDate;
};

// Note: Update is the tertiary state of notifying users that platform
// is Available but soon be Unavailable. It is only shown to users if
// they have the App open and the state changes.
const colorMap: Record<ParsedPlatformStateEnum | 'UPDATE', string> = {
  [parsedPlatformStateValues.UNAVAILABLE]: razzmatazz,
  [parsedPlatformStateValues.AVAILABLE_CANCEL_ONLY]: grapefruit,
  [parsedPlatformStateValues.AVAILABLE]: neonLemon,
  UPDATE: paradigmLightBlue,
};

const isUnavailable = (view: PlatformStateView) =>
  view.data.platformState === parsedPlatformStateValues.UNAVAILABLE;

const isCancelOnly = (view: PlatformStateView) =>
  view.data.platformState === parsedPlatformStateValues.AVAILABLE_CANCEL_ONLY;

// Note: State should not be backed by a style primative like color.
const isAndWasAvailable = (view: PlatformStateView) =>
  view.notification.color === paradigmLightBlue;

const getColor = (
  drfqStateView: PlatformStateView,
  grfqStateView: PlatformStateView,
): string => {
  if (isUnavailable(drfqStateView) || isUnavailable(grfqStateView))
    return colorMap[parsedPlatformStateValues.UNAVAILABLE];
  if (isCancelOnly(drfqStateView) || isCancelOnly(grfqStateView))
    return colorMap[parsedPlatformStateValues.AVAILABLE_CANCEL_ONLY];
  if (isAndWasAvailable(drfqStateView) || isAndWasAvailable(grfqStateView))
    return colorMap['UPDATE'];
  return colorMap[parsedPlatformStateValues.AVAILABLE];
};

const createView = (
  drfqStateView: PlatformStateView,
  grfqStateView: PlatformStateView,
): PlatformStateView => {
  const platformState = getMergedState(
    drfqStateView.data.platformState,
    grfqStateView.data.platformState,
  );
  const nextPlatformState = getMergedState(
    drfqStateView.data.nextPlatformState,
    grfqStateView.data.nextPlatformState,
  );

  const messageKind =
    drfqStateView.data.publishedAt! > grfqStateView.data.publishedAt!
      ? drfqStateView.data.messageKind
      : grfqStateView.data.messageKind;

  const publishedAt = getLatestDate(
    drfqStateView.data.publishedAt,
    grfqStateView.data.publishedAt,
  );

  const nextPlatformStateAt = getEarlierDate(
    drfqStateView.data.nextPlatformStateAt,
    grfqStateView.data.nextPlatformStateAt,
  );

  const getMergedStatus = (
    drfqStatus: FetcherStatus,
    grfqStatus: FetcherStatus,
  ): FetcherStatus => {
    if (drfqStatus === 'ERROR' || grfqStatus === 'ERROR') return 'ERROR';
    if (drfqStatus === 'LOADING' || grfqStatus === 'LOADING') return 'LOADING';
    return 'SYNCED';
  };

  return {
    status: getMergedStatus(drfqStateView.status, grfqStateView.status),
    data: {
      platformState,
      messageKind,
      publishedAt,
      nextPlatformState,
      nextPlatformStateAt,
    },
    notification: {
      color: getColor(drfqStateView, grfqStateView),
      hidePlatformState:
        drfqStateView.notification.hidePlatformState ||
        grfqStateView.notification.hidePlatformState,
      closable:
        drfqStateView.notification.closable &&
        grfqStateView.notification.closable,
      shouldNotify:
        drfqStateView.notification.shouldNotify ||
        grfqStateView.notification.shouldNotify,
    },
    error:
      drfqStateView.error !== '' ? drfqStateView.error : grfqStateView.error,
  };
};
